/**
 * New fermentation timeseries parser interface for EAV data structure
 * Implements Phase 2 of the SOW: Parser Interface Design
 */
/**
 * Base parser class with common functionality
 */
export class BaseFermentationParser {
    /**
     * Group a record stream into the per-fermentation shape `parseData` returns.
     * Lets a streaming parser express its logic once and satisfy both entry
     * points; note this necessarily buffers everything, so it is only for
     * callers that genuinely need the whole result.
     */
    async collectRecordings(stream) {
        const byFermentation = new Map();
        const order = [];
        for await (const { fermentationId, ...record } of stream) {
            let records = byFermentation.get(fermentationId);
            if (!records) {
                records = [];
                byFermentation.set(fermentationId, records);
                order.push(fermentationId);
            }
            records.push(record);
        }
        return order.map((fermentationId) => ({
            fermentationId,
            records: byFermentation.get(fermentationId),
        }));
    }
    /**
     * Iterate the rows of a delimited block in `[start, end)` without holding
     * them all in memory, parsing `windowSize` lines at a time.
     *
     * `parseWindow` must apply exactly the CSV settings the parser uses
     * elsewhere — the point is that windowing changes only *when* rows are
     * produced, never *what* they contain.
     *
     * Blank-line handling mirrors slicing the block and calling `.trim()` on it:
     * leading and trailing blanks are dropped, interior ones are preserved and
     * handed to the CSV parser as they would have been. That matters because
     * some parsers treat a row with no timestamp as end-of-data.
     */
    async *iterateDelimitedRows(text, start, end, parseWindow, windowSize = 5000) {
        let cursor = start;
        while (cursor < end && (text[cursor] === "\n" || text[cursor] === "\r"))
            cursor++;
        const headerEnd = text.indexOf("\n", cursor);
        if (headerEnd < 0 || headerEnd >= end)
            return;
        const header = text.slice(cursor, headerEnd).trim();
        cursor = headerEnd + 1;
        const window = [];
        let pendingBlanks = 0;
        const flush = async function* () {
            if (window.length === 0)
                return;
            const rows = await parseWindow(`${header}\n${window.join("\n")}`);
            window.length = 0;
            for (const row of rows)
                yield row;
        }.bind(this);
        while (cursor < end) {
            let lineEnd = text.indexOf("\n", cursor);
            if (lineEnd < 0 || lineEnd > end)
                lineEnd = end;
            const line = text.slice(cursor, lineEnd);
            cursor = lineEnd + 1;
            if (!line.trim()) {
                pendingBlanks++;
                continue;
            }
            // Only emit blanks once we know they are interior, not trailing.
            for (let i = 0; i < pendingBlanks; i++)
                window.push("");
            pendingBlanks = 0;
            window.push(line);
            if (window.length >= windowSize)
                yield* flush();
        }
        yield* flush();
    }
    /**
     * A per-source-column factor applied before the value is stored, for a
     * column whose unit differs from the one its attribute is registered in.
     *
     * Units are never part of an attribute name, so a parser that reads a
     * quantity in a different unit converts it here rather than inventing a
     * second attribute. Empty for a parser whose columns already match.
     */
    getAttributeScaling() {
        return {};
    }
    /**
     * Convert raw attribute values to standardized format.
     *
     * Declaration order in the mapping decides who wins when two source columns
     * name the same attribute: later entries overwrite earlier ones, and a
     * column the file does not carry writes nothing at all.
     */
    mapAttributes(rawData) {
        const mapping = this.getAttributeMapping();
        const scaling = this.getAttributeScaling();
        const result = {};
        for (const [equipmentName, standardName] of Object.entries(mapping)) {
            const value = rawData[equipmentName];
            if (value !== null && value !== undefined && !isNaN(Number(value))) {
                result[standardName] = Number(value) * (scaling[equipmentName] ?? 1);
            }
        }
        return result;
    }
    /**
     * Validate attribute values and return warnings for out-of-range values
     */
    validateAttributeValues(attributes) {
        const warnings = [];
        for (const [name, value] of Object.entries(attributes)) {
            if (name === "ph_pv" && (value < 0 || value > 14)) {
                warnings.push(`pH value ${value} is outside normal range (0-14)`);
            }
            else if (name === "temp_pv" && (value < -10 || value > 100)) {
                warnings.push(`Temperature ${value}°C is outside normal range (-10 to 100°C)`);
            }
            else if (name === "do_pv" && (value < 0 || value > 200)) {
                warnings.push(`DO value ${value}% is outside normal range (0-200%)`);
            }
            else if (name === "agit_pv" &&
                (value < 0 || value > 2000)) {
                warnings.push(`Agitation ${value} rpm is outside normal range (0-2000 rpm)`);
            }
        }
        return warnings;
    }
    /**
     * Helper to safely convert values to numbers
     */
    toNumber(value) {
        if (value === null || value === undefined || value === "") {
            return null;
        }
        const num = typeof value === "number" ? value : parseFloat(String(value));
        return isNaN(num) ? null : num;
    }
    /**
     * Helper to convert per-hour values to per-minute values
     */
    toPerMin(valuePerHour) {
        const num = this.toNumber(valuePerHour);
        return num !== null ? num / 60 : null;
    }
    /**
     * Helper to safely parse numeric string fields from Fermentation object
     */
    parseNumericField(value) {
        if (!value)
            return null;
        const num = parseFloat(value);
        return isNaN(num) ? null : num;
    }
}
//# sourceMappingURL=parser-interface.js.map